class CollectionProductsGrid extends HHBaseElement {
  constructor() {
    super()

    this.$grid = this.querySelector('.product-grid .grid')
    this.$loadMoreButton = this.querySelector('.js-load-more-button')
    this.$loadMoreCount = this.querySelector(
      '.collection-grid__load-more .current-amount'
    )

    this._handleLoadMore = this._handleLoadMore.bind(this)
  }

  connectedCallback() {
    this._attachListeners()
    this._setLoadMoreListners()
  }

  get nextPageUrl() {
    return this.getAttribute('data-paginate-next')
  }

  set nextPageUrl(value) {
    // paginateNext is null on the last page and setAttribute would
    // stringify it to "null", making the next fetch request ?page=null
    if (!value) return this.removeAttribute('data-paginate-next')
    return this.setAttribute('data-paginate-next', value)
  }

  get previousPageUrl() {
    return this.getAttribute('data-paginate-previous')
  }

  set previousPageUrl(value) {
    if (!value) return this.removeAttribute('data-paginate-previous')
    return this.setAttribute('data-paginate-previous', value)
  }

  _attachListeners() {
    this.$loadMoreButton?.addEventListener('click', this._handleLoadMore)
  }

  async _handleLoadMore(event) {
    event.preventDefault()
    return this._loadNextPage(true)
  }

  async _loadNextPage(moveFocus = false) {
    // The guard stays outside the try: an early return inside it would run the
    // finally and clear `loading` mid-fetch, letting the observer double-load.
    if (this.loading || !this.nextPageUrl) {
      return
    }

    try {
      this._setLoading(true)
      this.$loadMoreButton?.setAttribute('disabled', true)
      const nextPage = await this._fetchPage(this.nextPageUrl)
      if (!nextPage.paginateNext) {
        this.$loadMoreButton?.remove()
        this.$loadMoreButton = null
        this._observer?.disconnect()
      }

      this._renderPage(nextPage, false, moveFocus)
      // Only re-arm after a successful render. Re-arming in `finally` would
      // re-observe a still-visible sentinel after a failed fetch and retry the
      // same request forever; leaving it disarmed means the next scroll away
      // and back crosses the boundary and retries once, driven by the user.
      this._rearmObserver()
    } catch (error) {
      console.error(error)
    } finally {
      this.$loadMoreButton?.removeAttribute('disabled')
      this._setLoading(false)
    }
  }

  async _fetchPage(nextUrl) {
    if (nextUrl) {
      const response = await fetch(nextUrl)
      const $page = document.createElement('div')
      $page.innerHTML = await response.text()

      const collectionGrid = $page.querySelector('collection-products-grid')
      return {
        products: $page.querySelectorAll(
          'collection-products-grid .product-grid .product-card__list-item'
        ),
        $loadMoreButton: $page.querySelector(
          'collection-products-grid .js-load-more-button'
        ),
        paginateNext: collectionGrid.nextPageUrl,
        paginatePrevious: collectionGrid.previousPageUrl,
        filterContent: $page.querySelector('[data-filters-form]'),
        mobileFilterContent: $page.querySelector(
          '[data-filter-form-submitable]'
        ),
      }
    }

    throw new Error('No More Page to Load')
  }

  _renderPage(page, reset = false, moveFocus = true) {
    this.nextPageUrl = page.paginateNext
    this.previousPageUrl = page.paginatePrevious

    if (reset) {
      this.$grid.innerHTML = ''
      for (const item of page.products) {
        this.$grid.append(item)
      }
    } else {
      this._addProductsAndCards(page.products)
    }

    this.$loadMoreCount.innerHTML = [
      ...this.$grid.querySelectorAll('.product-card__list-item'),
    ].length

    if (page.filterContent) {
      this.$filtersForm.innerHTML = page.filterContent.innerHTML
      this.$filtersFormSubmitable.innerHTML = page.mobileFilterContent.innerHTML
    }

    // Scroll-triggered loads must not yank the viewport; the count element is
    // role="status" so the new total is announced either way.
    if (moveFocus && page.products.length) {
      page.products[0].focus()
    }
  }

  _addProductsAndCards(contents = []) {
    this.$grid.append(...contents)
  }

  _setLoadMoreListners() {
    if (this.$loadMoreButton) {
      this.$loadMoreButton.removeEventListener('click', this._handleLoadMore)
      this.$loadMoreButton.addEventListener('click', this._handleLoadMore)
    }

    this.$loadMoreContainer = this.querySelector('.collection-grid__load-more')

    this._observer?.disconnect()
    if (
      !this.hasAttribute('data-infinite-scroll') ||
      !this.$loadMoreContainer
    ) {
      return
    }

    // The button stays in the markup as the crawlable / no-JS path.
    if (this.$loadMoreButton) this.$loadMoreButton.style.display = 'none'
    // Back lands on page 1 at the top of the grid rather than a stale
    // offset into content that is no longer rendered. Full restore is its own ticket.
    history.scrollRestoration = 'manual'

    this._observer = new IntersectionObserver(
      (entries) =>
        entries.some((e) => e.isIntersecting) && this._loadNextPage(),
      { rootMargin: '400px 0px' }
    )
    this._observer.observe(this.$loadMoreContainer)
  }

  _rearmObserver() {
    if (!this._observer || !this.$loadMoreContainer || !this.nextPageUrl) return
    // Nothing crosses the boundary when the container is still in view after a
    // load, so re-observe to force a fresh callback.
    this._observer.unobserve(this.$loadMoreContainer)
    this._observer.observe(this.$loadMoreContainer)
  }

  _setLoading(loading) {
    this.loading = loading
  }
}

window.customElements.define('collection-products-grid', CollectionProductsGrid)
